import { PropertyRecord } from '@app/models/property.model';

export interface GeoBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface MapViewportChange {
  bounds: GeoBounds;
  userInitiated: boolean;
}

export function geoBoundsFromLatLngBounds(bounds: google.maps.LatLngBounds): GeoBounds {
  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  return {
    north: northEast.lat(),
    south: southWest.lat(),
    east: northEast.lng(),
    west: southWest.lng(),
  };
}

export function geoBoundsFromLiteral(bounds: google.maps.LatLngBoundsLiteral): GeoBounds {
  return {
    north: bounds.north,
    south: bounds.south,
    east: bounds.east,
    west: bounds.west,
  };
}

export function propertyInGeoBounds(property: PropertyRecord, bounds: GeoBounds): boolean {
  const coordinates = property.Coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return false;
  }

  const lng = Number(coordinates[0]);
  const lat = Number(coordinates[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return false;
  }

  return (
    lat >= bounds.south &&
    lat <= bounds.north &&
    lng >= bounds.west &&
    lng <= bounds.east
  );
}

export function serializeGeoBounds(bounds: GeoBounds | null): string | null {
  if (!bounds) {
    return null;
  }
  return `${bounds.south},${bounds.west},${bounds.north},${bounds.east}`;
}

export function parseGeoBounds(value: string | null | undefined): GeoBounds | null {
  if (!value?.trim()) {
    return null;
  }

  const parts = value.split(',').map((part) => Number(part.trim()));
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) {
    return null;
  }

  const [south, west, north, east] = parts;
  if (south > north || west > east) {
    return null;
  }

  return { south, west, north, east };
}

export function geoBoundsEqual(left: GeoBounds | null, right: GeoBounds | null): boolean {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }

  return (
    left.north === right.north &&
    left.south === right.south &&
    left.east === right.east &&
    left.west === right.west
  );
}
